import React from 'react'
import { ArrowLeft } from 'react-feather'
import { useNavigate } from 'react-router-dom'

import { HighlightedEmployerName } from '@frame/ui-components/highlighted-employer-name'
import { SlideUpPopover } from '@frame/ui-components/slide-up-popover'
import { Button } from '@frame/ui-primitives/button'
import { H3 } from '@frame/ui-primitives/h3'
import { P } from '@frame/ui-primitives/p'

import serviceDown from '@frame/images/service-down.svg'
import { TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { routes } from '@frame/routes'

type Props = {
  isOpen: boolean
  onClose: () => void
}

export function ServiceDown(props: Props) {
  const { isOpen, onClose } = props

  const navigate = useNavigate()
  const { bilEnabledStrategesNoDocuments } = useStrategies()
  const task = useDirectTask()

  const employerName = task?.employer_name || 'your employer'

  function handleClickBank() {
    navigate(`${routes.bankStatedInformation}/${task?.id}`)
  }

  const onlyOneAlternativeMethod =
    bilEnabledStrategesNoDocuments.length === 1 &&
    (bilEnabledStrategesNoDocuments[0] === TrueworkJsStrategy.BANK ||
      bilEnabledStrategesNoDocuments[0] === TrueworkJsStrategy.CREDENTIALS)

  function handleClickCredentials() {
    if (task?.id) {
      const params = new URLSearchParams()
      params.set('q', task?.employer_name || '')
      navigate(`${routes.companySearch}/${task?.id}?${params.toString()}`)
    } else {
      navigate(routes.companySearch)
    }
  }

  return (
    <>
      <SlideUpPopover show={isOpen} hide={onClose}>
        <div className="mb-4 flex items-center space-x-2">
          <Button aria-label="Back" onClick={onClose} Icon={ArrowLeft} border={false} intent="secondary" />
          <H3>Something went wrong</H3>
        </div>
        <div className="flex flex-col items-center self-stretch px-2 py-4">
          <img src={serviceDown} alt="Service Down" />
        </div>
        <div className="flex flex-col gap-5">
          <P>We've got your back! Please try again or choose an alternative method to show proof of income.</P>
          <>
            {bilEnabledStrategesNoDocuments.map((strategy, index) => {
              const isPrimary = index === 0

              const isBank = strategy === TrueworkJsStrategy.BANK
              const isCredentials = strategy === TrueworkJsStrategy.CREDENTIALS

              if (!isBank && !isCredentials) return null

              const buttonText = isBank ? 'Connect your bank accounts' : 'Log in to payroll account'

              if (onlyOneAlternativeMethod) {
                return (
                  <Button key={strategy} intent="primary" onClick={isBank ? handleClickBank : handleClickCredentials}>
                    {buttonText}
                  </Button>
                )
              }

              const title = isBank ? (
                <span>If you have multiple sources of income</span>
              ) : (
                <span>
                  If <HighlightedEmployerName employerName={employerName} /> is your only income
                </span>
              )

              return (
                <div
                  key={strategy}
                  className={`flex flex-col gap-3 rounded-md p-4 ${isPrimary ? 'bg-electric-200' : 'bg-moss-100'}`}
                >
                  <P className="font-semibold">{title}</P>
                  <Button
                    intent={isPrimary ? 'primary' : 'tertiary'}
                    onClick={isBank ? handleClickBank : handleClickCredentials}
                  >
                    {buttonText}
                  </Button>
                </div>
              )
            })}
          </>
        </div>
      </SlideUpPopover>
    </>
  )
}
